import {
  DataStatus,
  DialogFooter,
  Form,
  ButtonGroup,
  Button,
  FormButton,
  closeCurrentDialog,
  Separator,
  FormSubmitHandler,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {defaultTo, isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTreeFolderPathQuery} from '@dms/api/core';
import {
  PatchSupplierArticleRequest,
  useGetArticleQuery,
  useGetHandlingUnitQuery,
  useGetHandlingUnitsQuery,
  useGetSupplierArticleQuery,
  useGetSupplierDiscountGroupsQuery,
  usePatchSupplierArticleMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, useGetSupplierCustomersQuery} from '@dms/shared';
import {FALLBACK_HANDLING_UNIT} from '@dms/warehouse/shared';

import {generateHashFromObjects, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  SupplierArticlesAddNewForm,
  SupplierArticlesAddNewFormSchema,
} from '../types/SupplierArticlesAddNewForm';
import {SupplierArticlesAddNewArticleDetail} from './SupplierArticlesAddNewArticleDetail';
import {SupplierArticlesAddNewCatalogInfo} from './SupplierArticlesAddNewCatalogInfo';
import {SupplierArticlesAddNewQuantityAndUnitsMappingForm} from './SupplierArticlesAddNewQuantityAndUnitsMappingForm';
import {SupplierArticlesAddNewSupplierInfoForm} from './SupplierArticlesAddNewSupplierInfoForm';

interface SupplierArticlesAddNewProps extends RequiredTestIdProps {
  warehouseId: string;
  articleId: string;
  authorizationProfileId: string;
  onAfterSubmit: VoidFunction;
  supplierArticleId?: string;
  isEditing?: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function SupplierArticlesAddNew(props: SupplierArticlesAddNewProps) {
  const isEditing = defaultTo(false, props.isEditing);

  const {
    data: supplierArticle,
    isLoading: isSupplierArticleLoading,
    isError: hasSupplierArticleError,
  } = useGetSupplierArticleQuery(
    {supplierArticleId: props.supplierArticleId as string},
    {skip: isNilOrEmpty(props.supplierArticleId) || not(isEditing)}
  );

  const {
    articleName,
    articleManufacturerNumber,
    articleManufacturerName,
    articleStorageLocation,
    articleTreeFolderLeafId,
    isLoading: isArticleLoading,
    isFetching: isArticleFetching,
    isError: hasArticleError,
    refetch: refreshArticle,
  } = useGetArticleQuery(
    {warehouseId: props.warehouseId, articleId: props.articleId, isAclScoped: true},
    {
      skip: isNilOrEmpty(props.supplierArticleId) || not(isEditing),
      selectFromResult: ({data, isLoading, isFetching, isError}) => ({
        articleName: data?.name,
        articleManufacturerNumber: data?.manufacturerNumber,
        articleManufacturerName: data?.manufacturerName,
        articleStorageLocation: data?.storageLocation,
        articleTreeFolderLeafId: data?.treeFolder?.leafId,
        isLoading,
        isFetching,
        isError,
      }),
    }
  );

  const {
    data: treeFolderPath,
    isLoading: isTreeFolderPathLoading,
    isFetching: isTreeFolderPathFetching,
    isError: hasTreeFolderPathError,
  } = useGetTreeFolderPathQuery(
    {leafId: articleTreeFolderLeafId as string},
    {skip: isNilOrEmpty(articleTreeFolderLeafId)}
  );

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  const {
    data: handlingUnits,
    isLoading: areHandlingUnitsLoading,
    isError: hasHandlingUnitsError,
  } = useGetHandlingUnitsQuery();

  const {
    data: supplierUnit,
    isLoading: isSupplierUnitLoading,
    isError: hasSupplierUnitError,
  } = useGetHandlingUnitQuery(
    {id: supplierArticle?.supplierUnitId as string},
    {skip: isNilOrEmpty(supplierArticle?.supplierUnitId)}
  );

  const {
    data: warehouseUnit,
    isLoading: isWarehouseUnitLoading,
    isError: hasWarehouseUnitError,
  } = useGetHandlingUnitQuery(
    {id: supplierArticle?.warehouseUnitId as string},
    {skip: isNilOrEmpty(supplierArticle?.warehouseUnitId)}
  );

  const {
    data: supplierDiscountGroups,
    isLoading: areSupplierDiscountGroupsLoading,
    isError: hasSupplierDiscountGroupsError,
  } = useGetSupplierDiscountGroupsQuery(
    {
      supplierId: supplierArticle?.supplierId as string,
      authorizationProfileId: props.authorizationProfileId,
    },
    {skip: isNilOrEmpty(supplierArticle?.supplierId) || isNilOrEmpty(props.authorizationProfileId)}
  );

  const [patchSupplierArticle] = usePatchSupplierArticleMutation();

  const isLoading =
    isSupplierArticleLoading ||
    isArticleLoading ||
    isTreeFolderPathLoading ||
    areSuppliersLoading ||
    areHandlingUnitsLoading ||
    isSupplierUnitLoading ||
    isWarehouseUnitLoading ||
    areSupplierDiscountGroupsLoading;

  const hasError =
    hasSupplierArticleError ||
    hasArticleError ||
    hasTreeFolderPathError ||
    hasSuppliersError ||
    hasHandlingUnitsError ||
    hasSupplierUnitError ||
    hasWarehouseUnitError ||
    hasSupplierDiscountGroupsError;

  const defaultValues: DeepPartial<SupplierArticlesAddNewForm> = {
    supplierInfo: {
      supplierId: supplierArticle?.supplierId,
      supplierOrderingNumber: supplierArticle?.supplierOrderingNumber,
      supplierBulkPackageQuantity: supplierArticle?.supplierBulkPackageQuantity,
      supplierDiscountGroupCode: supplierArticle?.supplierArticleSupplierDiscountGroupCode,
      supplierManufacturerName: supplierArticle?.manufacturerName,
      supplierManufacturerCode: supplierArticle?.supplierManufacturerCode,
      recommendedPriceWithoutVat: supplierArticle?.recommendedPriceWithoutVat,
      recommendedPriceSource: supplierArticle?.recommendedPriceSource,
      purchasePriceWithoutVat: supplierArticle?.purchasePriceWithoutVat,
      purchasePriceSource: supplierArticle?.purchasePriceSource,
      currency: supplierArticle?.currency,
    },
    quantityAndUnitsMapping: {
      supplierQuantity: supplierArticle?.supplierQuantity,
      supplierUnitId: supplierArticle?.supplierUnitId,
      warehouseQuantity: supplierArticle?.warehouseQuantity,
      warehouseUnitId: supplierArticle?.warehouseUnitId,
    },
  };

  const handleSubmit: FormSubmitHandler<SupplierArticlesAddNewForm> = async (formValues) => {
    if (isNil(supplierArticle)) {
      // TODO We don't have POST yet
      return;
    }

    const patchRequestBody: PatchSupplierArticleRequest['body'] = {
      articleId: supplierArticle?.articleId as string,
      manufacturerNumber: supplierArticle?.manufacturerNumber as string,
      supplierArticle: {
        supplierId: formValues.supplierInfo.supplierId,
        supplierOrderingNumber: formValues.supplierInfo.supplierOrderingNumber,
        supplierBulkPackageQuantity: formValues.supplierInfo.supplierBulkPackageQuantity,
        supplierQuantity: formValues.quantityAndUnitsMapping.supplierQuantity,
        supplierUnitId: formValues.quantityAndUnitsMapping.supplierUnitId,
        warehouseQuantity: formValues.quantityAndUnitsMapping.warehouseQuantity,
        supplierDiscountGroupCode: formValues.supplierInfo.supplierDiscountGroupCode,
      },
    };

    await patchSupplierArticle({
      supplierArticleId: props.supplierArticleId as string,
      body: patchRequestBody,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .then(props.onAfterSubmit)
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={hasError} minHeight={155}>
      <Form<SupplierArticlesAddNewForm>
        key={generateHashFromObjects(supplierArticle)}
        experimentalZodSchema={SupplierArticlesAddNewFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <>
            <VStack spacing={4}>
              <SupplierArticlesAddNewSupplierInfoForm
                isEditing={props.isEditing}
                control={control}
                suppliers={suppliers}
                supplierDiscountGroups={supplierDiscountGroups}
                data-testid={suffixTestId('sections.supplierInfo', props)}
              />
              <Separator spacing={0} />
              <SupplierArticlesAddNewQuantityAndUnitsMappingForm
                control={control}
                handlingUnits={handlingUnits}
                supplierUnit={supplierUnit?.name ?? FALLBACK_HANDLING_UNIT}
                warehouseUnit={warehouseUnit?.name ?? FALLBACK_HANDLING_UNIT}
                data-testid={suffixTestId('sections.quantityAndUnitsMapping', props)}
              />
              <SupplierArticlesAddNewCatalogInfo
                supplierArticle={supplierArticle}
                data-testid={suffixTestId('sections.catalogInfo', props)}
              />
              <Show when={props.isEditing}>
                <SupplierArticlesAddNewArticleDetail
                  isArticleDetailFetching={isArticleFetching || isTreeFolderPathFetching}
                  articleDetail={{
                    name: articleName,
                    number: articleManufacturerNumber,
                    manufacturerName: articleManufacturerName,
                    storageLocation: articleStorageLocation,
                    categoryName: treeFolderPath?.treeFolder?.pathName,
                  }}
                  onRefreshArticleDetail={refreshArticle}
                  data-testid={suffixTestId('sections.articleDetail', props)}
                />
              </Show>
            </VStack>
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('actions.discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={
                    isEditing
                      ? i18n.t('general.actions.saveChanges')
                      : i18n.t('general.actions.add')
                  }
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Form>
    </DataStatus>
  );
}
